import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { publicSupabase } from "@/lib/public-supabase";
import { Cookie, Settings, Shield } from "lucide-react";

interface CookiePreferences {
  essential_cookies: boolean;
  analytics_cookies: boolean;
  marketing_cookies: boolean;
  preference_cookies: boolean;
}

export const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential_cookies: true,
    analytics_cookies: false,
    marketing_cookies: false,
    preference_cookies: false,
  });

  useEffect(() => {
    checkCookieConsent();
  }, []);

  const checkCookieConsent = async () => {
    // Check if user has already given consent via localStorage first (faster)
    const localConsent = localStorage.getItem('cookie_consent');
    if (localConsent) {
      try {
        const savedPrefs = JSON.parse(localConsent);
        setPreferences(savedPrefs);
        applyCookieSettings(savedPrefs);
        return;
      } catch {
        // Invalid JSON, continue to check database
      }
    }

    // Check database as fallback
    const sessionId = sessionStorage.getItem('session_id') || generateSessionId();
    sessionStorage.setItem('session_id', sessionId);

    try {
      // Check if any cookie consent exists for this session using privacy_consent table
      const { data, error } = await publicSupabase
        .from('privacy_consent')
        .select('*')
        .eq('session_id', sessionId)
        .eq('consent_type', 'cookies')
        .maybeSingle();

      if (error) {
        console.error('Error checking cookie consent:', error);
        setShowBanner(true);
        return;
      }

      if (!data) {
        setShowBanner(true);
      } else {
        // Parse preferences from the consented field or use defaults
        const savedPrefs = {
          essential_cookies: true,
          analytics_cookies: data.consented,
          marketing_cookies: data.consented,
          preference_cookies: data.consented,
        };
        setPreferences(savedPrefs);
        applyCookieSettings(savedPrefs);
        localStorage.setItem('cookie_consent', JSON.stringify(savedPrefs));
      }
    } catch (error) {
      console.error('Error checking cookie consent:', error);
      setShowBanner(true);
    }
  };

  const generateSessionId = () => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2);
  };

  const saveCookieConsent = async (prefs: CookiePreferences) => {
    const sessionId = sessionStorage.getItem('session_id') || generateSessionId();
    sessionStorage.setItem('session_id', sessionId);
    
    try {
      // Save to privacy_consent table
      const { error } = await publicSupabase
        .from('privacy_consent')
        .insert({
          session_id: sessionId,
          consent_type: 'cookies',
          consented: prefs.analytics_cookies || prefs.marketing_cookies || prefs.preference_cookies,
          user_agent: navigator.userAgent,
        });

      if (error) {
        console.error('Error saving to database:', error);
        // Still save locally and hide banner even if DB fails
      }

      // Always save to localStorage for faster future checks
      localStorage.setItem('cookie_consent', JSON.stringify(prefs));
      
      applyCookieSettings(prefs);
      setShowBanner(false);
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
      // Save locally anyway so the banner doesn't keep appearing
      localStorage.setItem('cookie_consent', JSON.stringify(prefs));
      applyCookieSettings(prefs);
      setShowBanner(false);
      setShowSettings(false);
    }
  };

  const applyCookieSettings = (prefs: CookiePreferences) => {
    const gtag = (window as any).gtag;
    const updateConsent = (consent: Record<string, "granted" | "denied">) => {
      try {
        if (typeof gtag === "function") {
          gtag("consent", "update", consent);
        }
      } catch (error) {
        console.warn("Failed to update consent mode:", error);
      }
    };

    // Essential cookies (always enabled)
    document.cookie = "essential=true; path=/; max-age=31536000; SameSite=Lax";

    // Analytics cookies
    if (prefs.analytics_cookies) {
      document.cookie = "analytics=true; path=/; max-age=31536000; SameSite=Lax";
      updateConsent({ analytics_storage: "granted" });
    } else {
      document.cookie = "analytics=false; path=/; max-age=31536000; SameSite=Lax";
      updateConsent({ analytics_storage: "denied" });
    }

    // Marketing cookies
    if (prefs.marketing_cookies) {
      document.cookie = "marketing=true; path=/; max-age=31536000; SameSite=Lax";
      updateConsent({ ad_storage: "granted" });
    } else {
      document.cookie = "marketing=false; path=/; max-age=31536000; SameSite=Lax";
      updateConsent({ ad_storage: "denied" });
    }

    // Preference cookies
    if (prefs.preference_cookies) {
      document.cookie = "preferences=true; path=/; max-age=31536000; SameSite=Lax";
    } else {
      document.cookie = "preferences=false; path=/; max-age=31536000; SameSite=Lax";
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      essential_cookies: true,
      analytics_cookies: true,
      marketing_cookies: true,
      preference_cookies: true,
    };
    setPreferences(allAccepted);
    saveCookieConsent(allAccepted);
  };

  const acceptEssentialOnly = () => {
    const essentialOnly = {
      essential_cookies: true,
      analytics_cookies: false,
      marketing_cookies: false,
      preference_cookies: false,
    };
    setPreferences(essentialOnly);
    saveCookieConsent(essentialOnly);
  };

  const saveCustomPreferences = () => {
    saveCookieConsent(preferences);
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
        <Card className="mx-auto max-w-4xl border-2 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <Cookie className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">We use cookies</h3>
                  <p className="text-sm text-muted-foreground">
                    We use cookies and similar technologies to enhance your browsing experience, 
                    analyze our traffic, and personalize content. By clicking "Accept All", you 
                    consent to our use of cookies.
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <Button onClick={acceptAll} size="sm">
                    Accept All
                  </Button>
                  <Button onClick={acceptEssentialOnly} variant="outline" size="sm">
                    Essential Only
                  </Button>
                  <Dialog open={showSettings} onOpenChange={setShowSettings}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Settings className="h-4 w-4 mr-2" />
                        Customize
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="flex items-center space-x-2">
                          <Shield className="h-5 w-5" />
                          <span>Cookie Preferences</span>
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium">Essential Cookies</h4>
                                <Badge variant="secondary">Required</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Necessary for the website to function properly. Cannot be disabled.
                              </p>
                            </div>
                            <Switch checked={true} disabled />
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <h4 className="font-medium">Analytics Cookies</h4>
                              <p className="text-sm text-muted-foreground">
                                Help us understand how visitors interact with our website.
                              </p>
                            </div>
                            <Switch
                              checked={preferences.analytics_cookies}
                              onCheckedChange={(checked) => updatePreference('analytics_cookies', checked)}
                            />
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <h4 className="font-medium">Marketing Cookies</h4>
                              <p className="text-sm text-muted-foreground">
                                Used to track visitors and display relevant advertisements.
                              </p>
                            </div>
                            <Switch
                              checked={preferences.marketing_cookies}
                              onCheckedChange={(checked) => updatePreference('marketing_cookies', checked)}
                            />
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <h4 className="font-medium">Preference Cookies</h4>
                              <p className="text-sm text-muted-foreground">
                                Remember your preferences and settings for a better experience.
                              </p>
                            </div>
                            <Switch
                              checked={preferences.preference_cookies}
                              onCheckedChange={(checked) => updatePreference('preference_cookies', checked)}
                            />
                          </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                          <Button variant="outline" onClick={() => setShowSettings(false)}>
                            Cancel
                          </Button>
                          <Button onClick={saveCustomPreferences}>
                            Save Preferences
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};